import { DragIndicatorRounded } from "@mui/icons-material";
import { Box, Stack, styled, SxProps, useTheme } from "@mui/material";
import React from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { AccordionViewList } from "./AccordionViewList";
import { ViewListColor } from "./ViewList";

const DroppableZone = styled(Stack)<{ $isDraggingOver: boolean; $backgroundColor: string }>`
  transition: ${({ theme }) => theme.transitions.create("background-color")};
  background-color: ${({ $isDraggingOver, $backgroundColor }) => ($isDraggingOver ? $backgroundColor : "transparent")};
  pointer-events: ${({ $isDraggingOver }) => ($isDraggingOver ? "none" : "auto")};
`;

const DragContainer = styled(Stack)<{
  $isDraggable: boolean;
  $isDragging: boolean;
  $backgroundColor: string;
}>`
  padding-left: ${({ $isDraggable, theme }) => ($isDraggable ? "0px" : theme.spacing(1))};
  transition: ${({ theme }) => theme.transitions.create("background-color")};
  background-color: ${({ $isDragging, $backgroundColor }) => ($isDragging ? $backgroundColor : undefined)};

  .dragHandle {
    opacity: 1;
    @media (pointer: fine) {
      opacity: ${({ $isDragging }) => ($isDragging ? 1 : 0)};
    }
  }

  &:hover {
    .dragHandle {
      opacity: 1;
    }
  }
`;

DragContainer.defaultProps = {
  direction: "row",
};

const DragHandle = styled(Box)<{
  $isDraggable: boolean;
  $isDisabled: boolean;
  $color: string;
  $backgroundColor: string;
}>`
  color: ${({ theme, $isDisabled }) => ($isDisabled ? "transparent" : theme.palette.grey[200])};
  background-color: "transparent";

  @media (pointer: fine) {
    color: ${({ $isDisabled, $color }) => ($isDisabled ? "transparent" : $color)};
    background-color: ${({ $isDisabled, $backgroundColor }) => ($isDisabled ? "transparent" : $backgroundColor)};
  }

  display: ${({ $isDraggable }) => ($isDraggable ? "flex" : "none")};
  align-self: stretch;
  align-items: center;
  justify-content: center;
  transition: ${({ theme }) => theme.transitions.create(["opacity"])};
  cursor: ${({ $isDisabled }) => ($isDisabled ? "default" : "pointer")};
  min-width: 20px;
`;

DragHandle.defaultProps = {
  className: "dragHandle",
};

interface IActiveItemsListProps<T> {
  activeItemsSortFunction: (a: T, b: T) => number;
}

interface IClosedItemsListProps<T> {
  closedItemsFilter: (item: T) => boolean;
  closedItemsSortFunction: (a: T, b: T) => number;
}

interface IProps<T> {
  droppableId: string;
  title: string;
  icon?: React.ReactNode;
  dataSource?: T[];
  isLoading: boolean;
  isExpanded?: boolean;
  isDragDisabled?: boolean;
  divider?: React.ReactNode;
  color?: ViewListColor;
  renderItem: (item: T, index: number, isClosed: boolean) => React.ReactNode;
  activeItemsListProps: IActiveItemsListProps<T>;
  closedItemsListProps: IClosedItemsListProps<T>;
  onAddClicked: (e: React.MouseEvent) => void;
  onExpandClicked?: () => void;
  onItemReordered: (itemGuid: string, sourceIndex: number, destinationIndex: number) => void;
  sx?: SxProps;
}

interface IDraggable {
  guid: string;
}

const DraggableAccordionViewList = <T extends IDraggable>(props: IProps<T>) => {
  const color = props.color ?? ViewListColor.Teal;
  const { droppableId, renderItem, activeItemsListProps, ...otherProps } = props;
  const isDragDisabled = props.isDragDisabled ?? true;
  const theme = useTheme();

  function handleDragEnd(result: DropResult) {
    const { draggableId: itemGuid, destination, source } = result;

    // User dragged the item outside the container
    if (!destination) {
      return;
    }

    // User dragged the item back to where it was
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    props.onItemReordered(itemGuid, source.index, destination.index);
  }

  function getDragContainerBackgroundColor(color: ViewListColor) {
    switch (color) {
      case ViewListColor.Orange:
        return "#fbe5d1";
      case ViewListColor.Teal:
      default:
        return "#e2edee";
    }
  }

  function getDroppableZoneBackgroundColor(color: ViewListColor) {
    switch (color) {
      case ViewListColor.Orange:
        return "#fdecdd";
      case ViewListColor.Teal:
      default:
        return "#ecf3f4";
    }
  }

  function getDragHandleColor(color: ViewListColor) {
    switch (color) {
      case ViewListColor.Orange:
        return theme.palette.orange.main;
      case ViewListColor.Teal:
      default:
        return "#198590";
    }
  }

  function getDragHandleBackgroundColor(color: ViewListColor) {
    switch (color) {
      case ViewListColor.Orange:
        return "#fadec4";
      case ViewListColor.Teal:
      default:
        return "#e2efef";
    }
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable key={props.droppableId} droppableId={props.droppableId}>
        {(provided, snapshot) => (
          <DroppableZone
            ref={provided.innerRef}
            $isDraggingOver={snapshot.isDraggingOver}
            $backgroundColor={getDroppableZoneBackgroundColor(color)}
            {...provided.droppableProps}
          >
            <AccordionViewList
              renderItem={(item, index, isClosed) => {
                if (isClosed) {
                  return (
                    <Box key={item.guid} sx={{ pl: 1 }}>
                      {props.renderItem(item, index, isClosed)}
                    </Box>
                  );
                } else {
                  return (
                    <Draggable key={item.guid} draggableId={item.guid} index={index} isDragDisabled={isDragDisabled}>
                      {(provided, snapshot) => (
                        <Box ref={provided.innerRef} {...provided.draggableProps}>
                          <DragContainer
                            $isDraggable={!isDragDisabled}
                            $isDragging={snapshot.isDragging}
                            $backgroundColor={getDragContainerBackgroundColor(color)}
                          >
                            <DragHandle
                              $isDraggable={!isDragDisabled}
                              $isDisabled={false}
                              $color={getDragHandleColor(color)}
                              $backgroundColor={getDragHandleBackgroundColor(color)}
                              {...provided.dragHandleProps}
                            >
                              <DragIndicatorRounded />
                            </DragHandle>
                            {props.renderItem(item, index, isClosed)}
                          </DragContainer>
                          {props.divider}
                        </Box>
                      )}
                    </Draggable>
                  );
                }
              }}
              activeItemsListProps={{
                draggablePlaceholder: provided.placeholder,
                divider: null,
                ...activeItemsListProps,
              }}
              {...otherProps}
            />
          </DroppableZone>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export { DraggableAccordionViewList };
